import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { useToast, Box, Tag, Stack, Text, Inline, IconTick } from "braid-design-system";
import { useJobDetailsViewContext, useAnalyticsHandlers } from "../../../CompanyProfile/CompanyProfile.mjs";
import { useSkillsMatchSignedInContext, onSkillsMatchAlert, onSkillsMatchProfileUpdated } from "../../../JobDetailsView/JobDetailsView.mjs";
import { translations } from "../../../../../components/SkillsMatch/.vocab/index.mjs";
import { translations as translations$1 } from "../../../../../components/SkillsMatch/SkillsMatchSignedIn/.vocab/index.mjs";
import { useCallback } from "react";
import { overflow } from "../SkillsMatchSignedIn.css.mjs";
const useSelectableQualities = (type) => {
  const { t } = useTranslations(translations);
  const updateErrorMessage = t(
    "There was a problem adding this item. Try refreshing or try again later."
  );
  const {
    addedQualities,
    updateAddedQualities,
    setIsSkillsLimitErrorShown,
    analyticsParams,
    updateToProfile
  } = useSkillsMatchSignedInContext();
  const { onAnalyticsEvent } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const sendAnalytics = useCallback(
    (actionType) => {
      const { keywords, totalMatchedQualities, totalAddedQualities } = analyticsParams;
      if (actionType === "error") {
        onSkillsMatchAlert({
          analytics,
          alertShownTone: "critical",
          alertShownMessage: updateErrorMessage
        });
      } else {
        onSkillsMatchProfileUpdated({
          analytics,
          type: actionType,
          keywords,
          totalMatchedQualities,
          totalAddedQualities
        });
      }
    },
    [analytics, analyticsParams, updateErrorMessage]
  );
  const selectedQualities = addedQualities[type];
  const { addToAddedQualities, removeFromAddedQualities } = updateAddedQualities[type];
  const _showToast = useToast();
  const showToast = useCallback(
    (toastType) => {
      let message;
      if (toastType === "added") {
        message = t("Added to profile");
      } else if (toastType === "removed") {
        message = t("Removed from profile");
      } else {
        message = updateErrorMessage;
      }
      _showToast({
        message,
        tone: toastType === "error" ? "critical" : "positive",
        key: `quality-${toastType}`
      });
    },
    [_showToast, t, updateErrorMessage]
  );
  const selectQuality = async (quality) => {
    var _a;
    addToAddedQualities(quality);
    const res = await ((_a = updateToProfile.add) == null ? void 0 : _a.call(updateToProfile, quality));
    if (res === "UpdateSkillSuccess") {
      showToast("added");
      sendAnalytics("add");
      return;
    }
    if (res === "SkillLimitReached") {
      setIsSkillsLimitErrorShown(true);
    } else if (res === "UpdateSkillError") {
      showToast("error");
      sendAnalytics("error");
    }
    removeFromAddedQualities(quality);
  };
  const deselectQuality = async (quality) => {
    var _a;
    removeFromAddedQualities(quality);
    const res = await ((_a = updateToProfile.remove) == null ? void 0 : _a.call(updateToProfile, quality));
    if (res === "UpdateSkillSuccess") {
      sendAnalytics("remove");
      showToast("removed");
      return;
    }
    showToast("error");
    sendAnalytics("error");
    addToAddedQualities(quality);
  };
  return {
    selectedQualities,
    selectQuality,
    deselectQuality
  };
};
const SelectableQualities = ({
  id,
  isExpanded,
  type
}) => {
  const { t } = useTranslations(translations);
  const { limit } = useSkillsMatchSignedInContext();
  const { qualities: allQualities } = useJobDetailsViewContext();
  const qualities = allQualities == null ? void 0 : allQualities[type];
  const { selectedQualities, selectQuality, deselectQuality } = useSelectableQualities(type);
  const displayedQualities = qualities == null ? void 0 : qualities.slice(0, limit);
  const hiddenQualities = !isExpanded ? qualities : qualities == null ? void 0 : qualities.slice(limit, qualities.length);
  const getCommonProps = (isAdded) => ({
    size: "small",
    ...isAdded && { icon: /* @__PURE__ */ jsx(IconTick, {}) }
  });
  const getLabelProps = (quality, isAdded) => {
    const text = quality.keyword.text;
    return isAdded ? {
      clearLabel: t("Remove {quality}", {
        quality: text
      }),
      onClear: () => deselectQuality(quality),
      state: "selected"
    } : {
      addLabel: t("Add {quality}", {
        quality: text
      }),
      onAdd: () => selectQuality(quality),
      state: "default"
    };
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    displayedQualities == null ? void 0 : displayedQualities.map((item) => {
      const qualityId = item.keyword.ontologyId;
      const text = item.keyword.text;
      const isAdded = selectedQualities.includes(item);
      const labelProps = getLabelProps(item, isAdded);
      const commonProps = getCommonProps(isAdded);
      return /* @__PURE__ */ jsx(Box, { className: overflow, children: /* @__PURE__ */ jsx(Tag, { ...commonProps, ...labelProps, children: text }) }, `${id}-${qualityId}`);
    }),
    isExpanded && (hiddenQualities == null ? void 0 : hiddenQualities.map((item) => {
      const qualityId = item.keyword.ontologyId;
      const text = item.keyword.text;
      const isAdded = selectedQualities.includes(item);
      const labelProps = getLabelProps(item, isAdded);
      const commonProps = getCommonProps(isAdded);
      return /* @__PURE__ */ jsx(
        Box,
        {
          className: overflow,
          children: /* @__PURE__ */ jsx(Tag, { ...commonProps, ...labelProps, children: text })
        },
        `${id}-expanded-${qualityId}`
      );
    }))
  ] });
};
const OtherQualities = ({
  inferred,
  unmatched,
  isExpanded,
  isInferredQualitiesTop,
  isUnmatchedQualitiesTop
}) => {
  const { t } = useTranslations(translations);
  const { t: tCommon } = useTranslations(translations$1);
  return /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    (inferred == null ? void 0 : inferred.length) !== 0 && !isInferredQualitiesTop && /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
      /* @__PURE__ */ jsx(Text, { size: "small", children: tCommon("Matches based on your career history") }),
      /* @__PURE__ */ jsx(Inline, { space: "xxsmall", children: /* @__PURE__ */ jsx(
        SelectableQualities,
        {
          id: "inferred-qualities",
          isExpanded,
          type: "inferred"
        }
      ) })
    ] }),
    (unmatched == null ? void 0 : unmatched.length) !== 0 && !isUnmatchedQualitiesTop && /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
      /* @__PURE__ */ jsx(Text, { size: "small", children: t("Not found in your profile") }),
      /* @__PURE__ */ jsx(Inline, { space: "xxsmall", children: /* @__PURE__ */ jsx(
        SelectableQualities,
        {
          id: "unmatched-qualities",
          isExpanded,
          type: "unmatched"
        }
      ) })
    ] })
  ] });
};
export {
  OtherQualities,
  SelectableQualities
};
